import { LitElement, html } from 'lit';
import { freeSampleCounts, addToCart } from '@Root/spree/api';
import { updateCartIndicator } from '@Root/spree/dom';
import { LIT_CSS_VARIABLES } from '@Root/lib/lit-styles.js';
import '@Root/lib/lit-3form-block/components/cta-button';
import {
  SHARED_CLASSES,
  SHARED_ELEMENTS,
} from '@Root/lib/lit-3form-block/styles.js';
import { styles } from './css';
import { logErrorToSentry } from '../../helpers/helpers';
class AddToCartModal extends LitElement {
  static properties = {
    loggedIn: {
      type: Boolean,
      state: true,
    },
    primaryCtaDisabled: { type: Boolean },
    _freeSampleCountResponse: { type: Boolean },
    title: { type: String },
    subtitle: { type: String },
    imageUrl: { type: String },
    productVariantId: { type: Number },
    _email: { type: String },
    _password: { type: String },
    _allowedFreeSamples: { type: String },
    _currentFreeSamples: { type: String },
    _displayPrice: { type: String },
    _isStudent: { type: Boolean },
    _showDisplayPrice: { type: Boolean },
    _loadingData: { type: Boolean },
    _addedToCart: { type: Boolean },
    _addingToCart: { type: Boolean },
  };

  constructor() {
    super();
    this.setDefaultDisplayValues();

    window.addEventListener('setupAddToCartData', e => {
      const details = e.detail || null;
      if (details) {
        this.setupSampleData(details);
      }
    });
  }

  setDefaultDisplayValues() {
    this.loggedIn = false;
    this.primaryCtaDisabled = true;
    this._freeSampleCountResponse = false;
    this.title = '';
    this.subtitle = '';
    this.imageUrl = '';
    this.productVariantId = null;
    this._email = '';
    this._password = '';
    this._allowedFreeSamples = null;
    this._currentFreeSamples = null;
    this._displayPrice = null;
    this._isStudent = false;
    this._showDisplayPrice = false;
    this._addedToCart = false;
    this._addingToCart = false;
  }

  subtitleHTML(details) {
    const sampleType = details.sampleType || '';
    const sampleTypeFormatted =
      sampleType[0].toUpperCase() + sampleType.substring(1, sampleType.length);
    const size =
      typeof details.productSize === 'string'
        ? details.productSize
        : details.productSize?.attributes.value;
    return html`
      ${size} ${sampleTypeFormatted} Sample
      ${!!details.gaugeStr
        ? html`
            <br />
            ${details.gaugeStr}
          `
        : html``}
    `;
  }

  async connectedCallback() {
    super.connectedCallback();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  setupSampleData(details) {
    this._loadingData = true;
    this.title = details.headerStr;
    this.subtitle = this.subtitleHTML(details);
    this.imageUrl = details.imageUrl;
    this.productVariantId = details.productVariantId;
    if (this.hasTfUserCookie) {
      this.setupSampleCountsData();
    }
  }

  setupSampleCountsData() {
    freeSampleCounts(this.productVariantId).then(response => {
      if (response) {
        this._freeSampleCountResponse = true;
        this._currentFreeSamples = response.current_free_samples;
        this._allowedFreeSamples = response.allowed_free_samples;
        this._displayPrice = response.display_price;
        this._isStudent = response.is_student;
        if (this._isStudent) this._allowedFreeSamples = 0;
        this._showDisplayPrice =
          this._allowedFreeSamples !== null &&
          Number(this._currentFreeSamples) >= Number(this._allowedFreeSamples);
        this.primaryCtaDisabled = false;
      }
    });
  }

  static get styles() {
    return [LIT_CSS_VARIABLES, SHARED_ELEMENTS, SHARED_CLASSES, styles];
  }

  render() {
    return this.hasTfUserCookie ? this.addToCartHTML : this.loginHTML;
  }

  get hasTfUserCookie() {
    return document.cookie.indexOf('tf-user') > -1;
  }

  get addToCartHTML() {
    return html`
      <section class="add-to-cart">
        ${this.contentHeaderHTML}
        <div class="content-wrapper">
          <div class="image-wrapper">
            <img src="${this.imageUrl}" alt="Product Image" />
          </div>
          <div class="details-wrapper">
            <div class="headers">
              <h2>${this.title}</h2>
              <h3 class="description">${this.subtitle}</h3>
            </div>
            ${this._showDisplayPrice
              ? html`
                  <h3 class="price">${this.priceString}</h3>
                `
              : html``}
            ${this._isStudent
              ? this.studentSamplesPrompt
              : this.sampleLimitsString}
          </div>
        </div>
        <div class="ctas-wrapper">
          ${this.ctaButtonsList.map(button => {
            if (button.ctaType === 'primary') {
              return html`
                <cta-button
                  @click="${() => this.addToCart()}"
                  cta-type="${button.ctaType}"
                  cta-data=${JSON.stringify(button.ctaData)}
                  .disabled="${this.primaryCtaDisabled || this._addingToCart}"
                ></cta-button>
              `;
            } else {
              return html`
                <cta-button
                  cta-type="${button.ctaType}"
                  cta-data=${JSON.stringify(button.ctaData)}
                ></cta-button>
              `;
            }
          })}
        </div>
      </section>
    `;
  }

  get contentHeaderHTML() {
    return html`
      <div class="content-wrapper-header">
        <p class="description added-to-cart">
          ${this._addedToCart ? 'Added to cart' : ''}
        </p>
        <span class="close-modal" @click="${() => this.closeModal()}">
          <img src="/static/icons/SVG/close.svg" />
        </span>
      </div>
    `;
  }

  get priceString() {
    return this._freeSampleCountResponse ? `${this._displayPrice} each` : ``;
  }

  get sampleLimitsString() {
    return this._freeSampleCountResponse && this._allowedFreeSamples !== null
      ? html`
          <p class="description sample-limit">
            ${`First ${this._allowedFreeSamples} samples complimentary. Additional Samples ${this._displayPrice} ea.`}
          </p>
        `
      : html``;
  }

  get studentSamplesPrompt() {
    return html`
      <p class="description sample-limit">
        Students receive a 70% discount on samples. You can also contact your
        local
        <a class="contact-rep" @click=${() => this.openContactRep()}
          >3form sales rep</a
        >
        for free samples that can be dropped off at your school
      </p>
    `;
  }

  openContactRep() {
    MicroModal.show('modal-contact-rep');
  }

  closeModal() {
    this.setDefaultDisplayValues();
    MicroModal.close('modal-request-sample');
  }

  get ctaButtonsList() {
    const list = [];
    // TODO: if (isSampleOrder) {
    list.push({
      ctaType: 'primary',
      ctaData: {
        text: 'Add to Cart',
        fullWidth: true,
      },
    });
    list.push({
      ctaType: 'secondary',
      ctaData: {
        text: 'View Cart',
        url: `${window.SpreeAPI.host}/cart`,
        fullWidth: true,
      },
    });
    // }
    return list;
  }

  async addToCart() {
    this._addingToCart = true;
    this.cart = await addToCart(this.productVariantId, 1);
    if (this.cart) {
      updateCartIndicator(this.cart.item_count);
      this.setupSampleCountsData();
      this._addingToCart = false;
      this._addedToCart = true;
    }
  }

  get loginHTML() {
    return html`
      <section class="login-content">
        ${this.contentHeaderHTML}
        <h2>Log in to Order Samples</h2>
        <form class="login">
          <div class="inputs">
            <span class="input-wrapper" id="email-wrapper">
              <input
                type="text"
                id="email"
                name="email"
                .value="${this._email}"
                @change="${this.emailChanged}"
                @keyup="${this.emailChanged}"
              />
              <label for="email" class="input-label">
                Email
              </label>
            </span>
            <span class="input-wrapper" id="password-wrapper">
              <input
                type="password"
                id="password"
                name="password"
                .value="${this._password}"
                @change="${this.passwordChanged}"
                @keyup="${this.passwordChanged}"
              />
              <label for="password" class="input-label">
                Password
              </label>
            </span>
          </div>
          <span class="submit-wrapper">
            <cta-button
              class="submit-btn"
              cta-data=${this.submitCtaData}
              cta-type="primary"
              .disabled=${!this.validLoginInputs}
              @click=${() => this.tryLogin()}
            ></cta-button>
          </span>
        </form>
        <cta-text cta-data=${this.createAccountCtaData}></cta-text>
        <cta-text cta-data=${this.resetPasswordCtaData}></cta-text>
      </section>
    `;
  }

  get validLoginInputs() {
    return !!this._email && !!this._password;
  }

  passwordChanged(event) {
    if (event) {
      this._password = event.target.value;
    }
  }

  emailChanged(event) {
    if (event) {
      this._email = event.target.value;
    }
  }

  tryLogin() {
    if (!this.validLoginInputs) return;
    const headers = {
        'Content-Type': 'application/json',
        'Access-Control-Request-Headers': 'set-cookie',
      },
      data = {
        method: 'POST',
        redirect: 'manual',
        credentials: 'include',
        headers,
        body: JSON.stringify({
          spree_user: {
            email: this._email,
            password: this._password,
          },
        }),
      };

    fetch(`${window.SpreeAPI.host}/login`, data)
      .then(response => {
        this.setupSampleCountsData();
        return this.hasTfUserCookie;
      })
      .then(body => {
        this.handleResponseOK();
      })
      .catch(error => {
        this.handleRequestError(error);
      });
  }

  handleResponseOK() {
    this.requestUpdate();
    this.setupSampleCountsData();
  }

  handleRequestError(error = {}) {
    this.requestUpdate();
    logErrorToSentry(error);
  }

  get createAccountCtaData() {
    return JSON.stringify({
      text: 'Create an Account',
      url: '/userprofile',
    });
  }

  get resetPasswordCtaData() {
    return JSON.stringify({
      text: 'Reset Password',
      url: '/userprofile/reset',
    });
  }

  get submitCtaData() {
    return JSON.stringify({
      text: 'Log In',
      fullWidth: true,
    });
  }
}

customElements.define('add-to-cart-modal', AddToCartModal);
